import type { Theme } from '@mui/material';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PreceonBadge from '/@/components/crop-monitoring/preceon-badge';
import { useUnitProvider } from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { Brand } from '/@/types/catalog';
import type { Units } from '/@/types/core';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';
import { getHybridProductName, getSharedHarvestHybrids } from '/@/utils/crop-monitoring';

/**
 * The hybrids sharing the harvest, one row each with its own planting data: the harvest
 * (yield, humidity, harvested area) is the field's, but each hybrid was planted on its own.
 * Not rendered for a single hybrid, whose planting data stays in the header.
 */
const ReportHybrids = ({
    cropMonitoring,
    unitsProp,
}: {
    cropMonitoring: CropMonitoring;
    unitsProp?: { units: Units; language: string; brands: Brand[] };
}) => {
    const units = useUnitProvider(unitsProp);
    const hybrids = getSharedHarvestHybrids(cropMonitoring);
    if (!hybrids.length) return null;

    const crop = cropMonitoring.crop;
    const harvestDate = cropMonitoring.harvestDate ? dayjs(cropMonitoring.harvestDate) : null;
    const columns = [
        _('Híbrido'),
        _('Fecha de siembra'),
        _('Densidad de siembra'),
        _('Área sembrada'),
        _('Días a cosecha'),
    ];

    return (
        <Stack spacing={1} sx={{ p: 2, border: (t: Theme) => `1px solid ${t.palette.divider}` }}>
            <Typography color='primary' fontWeight='semibold' variant='h6'>
                {_('Híbridos')}
            </Typography>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell key={column}>
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {column}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {hybrids.map((hybrid) => (
                        <TableRow key={hybrid.id}>
                            <TableCell>
                                <Stack direction='row' spacing={1} alignItems='center' useFlexGap flexWrap='wrap'>
                                    <Typography variant='body2' color='primary' fontWeight='bold'>
                                        {getHybridProductName(hybrid) || '-'}
                                    </Typography>
                                    {hybrid.cornCropMonitoring?.isPreceon && (
                                        <PreceonBadge sx={{ borderRadius: '8px', px: 1, py: 0.25, fontSize: 12 }} />
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Typography variant='body2' color='primary'>
                                    {hybrid.plantingDate ? formatDate(new Date(hybrid.plantingDate)) : '-'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant='body2' color='primary'>
                                    {hybrid.density
                                        ? `${units.toDensity(hybrid.density, crop, 2).value} ${units.getUnit('density', crop)}`
                                        : '-'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant='body2' color='primary'>
                                    {hybrid.plantedArea
                                        ? `${units.toArea(hybrid.plantedArea, 2).value} ${units.getUnit('area')}`
                                        : '-'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant='body2' color='primary'>
                                    {harvestDate && hybrid.plantingDate
                                        ? harvestDate.diff(dayjs(hybrid.plantingDate), 'day')
                                        : '-'}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default ReportHybrids;
